import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import {
  type ActiveBooking,
  type PendingPairing,
  type PendingPairings,
  type PlacedPairing,
  pairingsByHostDay,
} from "@/app/booking-calendar/lib/rescheduleTargets"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import type { ManualResolvePairing } from "../../../schedule-overrides/scheduleOverridesApi"

// ═══════════════════════════════════════════════════════════════════
//  PAIRING STATE
//
//  The manual reschedule workspace's (AB#811, SU10) unapplied choices:
//  which affected booking is being placed, and where each paired one is
//  to move. Pure — the page keeps it in React state and nothing else, so
//  a reload drops it (decision ams-1rd: warn before leaving instead).
// ═══════════════════════════════════════════════════════════════════

/** One pairing not yet applied: the host and start a booking moves to. */
export interface Pairing extends PendingPairing {
  /** The target host's name, for the dulled row on the left. */
  hostLabel: string
}

export interface PairingState {
  /** The booking request being placed; null when none is. */
  activeId: string | null
  /** By booking request id: each booking at most once. */
  pairings: Readonly<Record<string, Pairing>>
}

export const EMPTY_PAIRING_STATE: PairingState = { activeId: null, pairings: {} }

/** Whether any pairing is waiting to be applied — what the leave guard and Apply turn on. */
export function hasPairings(state: PairingState): boolean {
  return Object.keys(state.pairings).length > 0
}

/** How many pairings are waiting. */
export function pairingCount(state: PairingState): number {
  return Object.keys(state.pairings).length
}

/** Makes a booking the active one, or — clicked again — none. */
export function toggleActive(state: PairingState, requestId: string): PairingState {
  return { ...state, activeId: state.activeId === requestId ? null : requestId }
}

/**
 * Pairs a booking with a target. Replaces an earlier pairing of the same booking, and leaves no booking
 * active when it was the active one: the next click picks the next booking.
 */
export function pair(state: PairingState, requestId: string, target: Pairing): PairingState {
  return {
    activeId: state.activeId === requestId ? null : state.activeId,
    pairings: { ...state.pairings, [requestId]: target },
  }
}

/** Undoes a booking's pairing; unchanged when it has none. */
export function unpair(state: PairingState, requestId: string): PairingState {
  if (!(requestId in state.pairings)) return state
  return { ...state, pairings: Object.fromEntries(Object.entries(state.pairings).filter(([id]) => id !== requestId)) }
}

/**
 * A click on a booking on the left: a paired booking's pairing is undone and the booking becomes the
 * active one again, ready to be placed elsewhere; an unpaired one toggles active.
 */
export function clickBooking(state: PairingState, requestId: string): PairingState {
  if (requestId in state.pairings) return { ...unpair(state, requestId), activeId: requestId }
  return toggleActive(state, requestId)
}

/**
 * Keeps only the bookings the override still lists: one moved (or otherwise dealt with) since leaves
 * the list, and so leaves the state.
 */
export function keepListed(state: PairingState, listed: readonly string[]): PairingState {
  const ids = new Set(listed)
  const kept = Object.entries(state.pairings).filter(([id]) => ids.has(id))
  const activeId = state.activeId && ids.has(state.activeId) ? state.activeId : null
  if (kept.length === Object.keys(state.pairings).length && activeId === state.activeId) return state
  return { activeId, pairings: Object.fromEntries(kept) }
}

/** The pairings as targetValidity reads them. */
export function pendingPairings(state: PairingState): PendingPairings {
  return state.pairings
}

/** The pairings by target host and Pacific day, for targetValidity: computed once per change. */
export function pairingsByHostDayOf(
  state: PairingState,
  timeZone: string = SITE_TIME_ZONE,
): ReadonlyMap<string, readonly PlacedPairing[]> {
  return pairingsByHostDay(state.pairings, timeZone)
}

/** The body of POST …/resolve/manual: the pairings in the order they were made. */
export function toSubmission(state: PairingState): ManualResolvePairing[] {
  return Object.entries(state.pairings).map(([bookingRequestId, p]) => ({
    bookingRequestId,
    targetCalUserId: p.calUserId,
    targetStart: p.start,
  }))
}

/** What a workspace booking needs to be placed; null when the server did not send it. */
export function activeBookingOf(booking: BookingDetails): ActiveBooking | null {
  if (!booking.requestId || !booking.teamSlug || !booking.eventTypeSlug) return null
  const length = Math.round((Date.parse(booking.end) - Date.parse(booking.start)) / 60_000)
  return {
    requestId: booking.requestId,
    teamSlug: booking.teamSlug,
    eventTypeSlug: booking.eventTypeSlug,
    durationMinutes: Number.isFinite(length) && length > 0 ? length : 1,
    currentStart: booking.start,
    currentCalUserId: booking.hosts[0]?.id ?? null,
    label: customerLabel(booking),
  }
}

/** The customer's name as the override's detail lists it, or a stand-in. */
export function customerLabel(booking: BookingDetails): string {
  return booking.attendees[0]?.name?.trim() || "Appointment"
}
